import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import WorkplaceNameDisplay from '../../components/WorkplaceNameDisplay';
import { workplaceApi, riskAnalysisApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './RiskAnalysisPage.css';

interface Workplace {
  id: number;
  title?: string;
  name?: string;
  address?: string;
  dangerClass?: 'low' | 'dangerous' | 'veryDangerous';
  riskLevel?: 'low' | 'dangerous' | 'veryDangerous' | 'very_dangerous';
  sskRegistrationNo?: string;
  sgkNo?: string;
  city?: string;
  district?: string;
  employeeCount?: number;
  contactPerson?: string;
  employerName?: string;
  destekElemani?: string;
  calisanTemsilcisi?: string;
  doctorName?: string;
  doctorDiplomaNo?: string;
  expertName?: string;
  expertCertificateNo?: string;
}

interface SubSector {
  id: string;
  name: string;
  riskCount: number;
}

interface Sector {
  id: string;
  name: string;
  icon: string;
  riskCount: number;
  isHierarchical?: boolean;
  subSectors?: SubSector[];
}

interface Risk {
  id: string;
  kategori: string;
  tehlikeKaynagi: string;
  tehlike: string;
  riskAciklamasi?: string;
  olasiSonuclar: string;
  mevcutOnlemler: string[] | string;
  onerilen: string[] | string;
  kanunMaddeleri?: any[];
  defaultOlasilik?: number;
  defaultSiddet?: number;
  defaultFrekans?: number;
  olasilik: number;
  siddet: number;
  frekans: number;
  selected?: boolean;
  isCustom?: boolean;
}

const MATRIX_OPTIONS = [
  { id: 'lMatrix', label: 'L Matrisi', desc: 'Olasılık × Şiddet (en yaygın standart yöntem)' },
  { id: '5x5', label: '5×5 Matris', desc: 'Olasılık × Şiddet (5 kademeli Türk İSG standardı)' },
  { id: 'fineKinney', label: 'Fine-Kinney', desc: 'Olasılık × Şiddet × Frekans (üçlü risk skoru)' },
  { id: 'fmea', label: 'FMEA', desc: 'Hata Türü ve Etki Analizi (RPN)' }
];

const RISK_LEVEL_LABEL: Record<string, string> = {
  low: 'Az Tehlikeli',
  dangerous: 'Tehlikeli',
  veryDangerous: 'Çok Tehlikeli',
  very_dangerous: 'Çok Tehlikeli'
};

const VALIDITY_YEARS: Record<string, number> = {
  low: 6,
  dangerous: 4,
  veryDangerous: 2,
  very_dangerous: 2
};

function calcScore(r: Risk, matrixType: string) {
  if (matrixType === 'fineKinney') return (r.olasilik || 1) * (r.siddet || 1) * (r.frekans || 1);
  return (r.olasilik || 1) * (r.siddet || 1);
}

function scoreLabel(score: number, matrixType: string) {
  if (matrixType === 'fineKinney') {
    if (score <= 20) return { label: 'Önemsiz', cls: 'level-negligible' };
    if (score <= 70) return { label: 'Düşük', cls: 'level-low' };
    if (score <= 200) return { label: 'Orta', cls: 'level-medium' };
    if (score <= 400) return { label: 'Yüksek', cls: 'level-high' };
    return { label: 'Çok Yüksek', cls: 'level-critical' };
  }
  // 5x5 ve L Matrisi
  if (score <= 4) return { label: 'Önemsiz', cls: 'level-negligible' };
  if (score <= 9) return { label: 'Düşük', cls: 'level-low' };
  if (score <= 16) return { label: 'Orta', cls: 'level-medium' };
  if (score <= 20) return { label: 'Yüksek', cls: 'level-high' };
  return { label: 'Çok Yüksek', cls: 'level-critical' };
}

function validityDate(riskLevel: string) {
  const yrs = VALIDITY_YEARS[riskLevel] || 4;
  const d = new Date();
  d.setFullYear(d.getFullYear() + yrs);
  return d.toLocaleDateString('tr-TR');
}

const BLANK_CUSTOM: Omit<Risk, 'id'> = {
  kategori: '',
  tehlikeKaynagi: '',
  tehlike: '',
  olasiSonuclar: '',
  mevcutOnlemler: '',
  onerilen: '',
  kanunMaddeleri: [],
  olasilik: 3,
  siddet: 3,
  frekans: 3,
  selected: true,
  isCustom: true
};

const SCORE_LEVELS = ['Önemsiz', 'Düşük', 'Orta', 'Yüksek', 'Çok Yüksek'];
const SCORE_CLS = ['level-negligible', 'level-low', 'level-medium', 'level-high', 'level-critical'];

const normalizeSearch = (s: string) =>
  (s || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/i̇/g, 'i')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .trim();

export const RiskAnalysisPage: React.FC = () => {
  const navigate = useNavigate();
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  const [step, setStep] = useState<number>(1);
  const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
  const [wpLoading, setWpLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [selectedWp, setSelectedWp] = useState<Workplace | null>(null);

  // Onay Heyeti / Risk Değerlendirme Ekibi
  const [expertName, setExpertName] = useState<string>('');
  const [expertCertNo, setExpertCertNo] = useState<string>('');
  const [doctorName, setDoctorName] = useState<string>('');
  const [doctorDipNo, setDoctorDipNo] = useState<string>('');
  const [employerName, setEmployerName] = useState<string>('');
  const [destekElemani, setDestekElemani] = useState<string>('');
  const [calisanTemsilcisi, setCalisanTemsilcisi] = useState<string>('');

  // Step 2: Matrix Type
  const [matrixType, setMatrixType] = useState<string>('lMatrix');

  // Step 3: Sectors
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [selectedSector, setSelectedSector] = useState<Sector | null>(null);
  const [selectedSubSectorIds, setSelectedSubSectorIds] = useState<string[]>([]);
  const [sectorSearch, setSectorSearch] = useState<string>('');
  const [sectorLoading, setSectorLoading] = useState<boolean>(false);
  const [sectorsLoading, setSectorsLoading] = useState<boolean>(true);

  // Step 4: Risks
  const [activeRisks, setActiveRisks] = useState<Risk[]>([]);
  const [riskSearch, setRiskSearch] = useState<string>('');
  const [filterKategori, setFilterKategori] = useState<string>('');
  const [filterSeviye, setFilterSeviye] = useState<string>('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Custom Risk Form
  const [showCustomForm, setShowCustomForm] = useState<boolean>(false);
  const [customRisk, setCustomRisk] = useState<Omit<Risk, 'id'>>({ ...BLANK_CUSTOM });
  const [customRiskErrors, setCustomRiskErrors] = useState<Record<string, string>>({});

  // Step 5: Generate
  const [legalChecked, setLegalChecked] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [generatingExcel, setGeneratingExcel] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const scrollToTop = () => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
  };

  useEffect(() => {
    scrollToTop();
  }, [step]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch Workplaces
  useEffect(() => {
    fetchWorkplaces();
  }, []);

  const fetchWorkplaces = async () => {
    try {
      setWpLoading(true);
      const res = await workplaceApi.getAll();
      const list = (res && res.data) ? res.data : (Array.isArray(res) ? res : []);
      setWorkplaces(list.map((wp: any) => ({
        id: wp.id,
        name: wp.title || wp.name || `İşyeri #${wp.id}`,
        title: wp.title || wp.name,
        address: wp.address || [wp.district, wp.city].filter(Boolean).join(' / ') || '',
        riskLevel: wp.dangerClass || wp.riskLevel || 'dangerous',
        dangerClass: wp.dangerClass || wp.riskLevel || 'dangerous',
        sgkNo: wp.sgkNo || wp.sskRegistrationNo || '',
        sskRegistrationNo: wp.sgkNo || wp.sskRegistrationNo || '',
        city: wp.city,
        district: wp.district,
        employeeCount: wp.employeeCount,
        contactPerson: wp.contactPerson
      })));
    } catch (e) {
      console.error('Workplace fetch error:', e);
      setError('İşyerleri yüklenirken hata oluştu.');
    } finally {
      setWpLoading(false);
    }
  };

  // Fetch Sectors
  const loadSectors = useCallback(async () => {
    try {
      setSectorsLoading(true);
      const data = await riskAnalysisApi.getSectors();
      if (data && data.sectors && Array.isArray(data.sectors)) {
        setSectors(data.sectors);
      }
    } catch (err) {
      console.error('Sectors loading error:', err);
    } finally {
      setSectorsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSectors();
  }, [loadSectors]);

  useEffect(() => {
    if (step === 3 && sectors.length === 0 && !sectorsLoading) {
      loadSectors();
    }
  }, [step, sectors.length, sectorsLoading, loadSectors]);


  const loadSectorRisks = useCallback(async (sectorId: string | string[]) => {
    setSectorLoading(true);
    try {
      const id = Array.isArray(sectorId) ? sectorId.join(',') : sectorId;
      const data = await riskAnalysisApi.getSectorRisks(id);
      const risks: Risk[] = (data.riskler || []).map((r: any) => ({
        ...r,
        olasilik: r.defaultOlasilik || r.olasilik || 3,
        siddet: r.defaultSiddet || r.siddet || 3,
        frekans: r.defaultFrekans || r.frekans || 3,
        selected: true
      }));
      setActiveRisks(risks);
    } catch (err) {
      console.error('Error loading sector risks:', err);
      setError('Sektör riskleri yüklenirken hata oluştu.');
    } finally {
      setSectorLoading(false);
    }
  }, []);

  // Kullanıcı bilgileri yüklendiğinde uzman alanlarını ön tanımlı doldur
  useEffect(() => {
    if (user?.fullName && !expertName) {
      setExpertName(user.fullName);
    }
    const cert = (user as any)?.certificateNo || (user as any)?.sicilNo;
    if (cert && !expertCertNo) {
      setExpertCertNo(cert);
    }
  }, [user]);

  const handleSelectWorkplace = (wp: Workplace) => {
    setSelectedWp(wp);
    setSearchQuery(wp.name || wp.title || '');
    setDropdownOpen(false);

    // Onay heyeti bilgilerini işyerinden ve mevcut oturumdan otomatik doldur
    setEmployerName(wp.employerName || wp.contactPerson || '');
    setDestekElemani(wp.destekElemani || '');
    setCalisanTemsilcisi(wp.calisanTemsilcisi || '');
    setDoctorName(wp.doctorName || '');
    setDoctorDipNo(wp.doctorDiplomaNo || '');
    setExpertName(wp.expertName || user?.fullName || 'İş Güvenliği Uzmanı');
    setExpertCertNo(wp.expertCertificateNo || (user as any)?.certificateNo || (user as any)?.sicilNo || '');
  };

  const filteredWorkplaces = useMemo(() => {
    const q = normalizeSearch(searchQuery);
    if (!q) return workplaces;
    return workplaces.filter(wp => {
      const nameStr = normalizeSearch(wp.name || wp.title || '');
      const sgkStr = normalizeSearch(wp.sskRegistrationNo || wp.sgkNo || '');
      const cityStr = normalizeSearch(wp.city || '');
      const distStr = normalizeSearch(wp.district || '');
      return nameStr.includes(q) || sgkStr.includes(q) || cityStr.includes(q) || distStr.includes(q);
    });
  }, [workplaces, searchQuery]);

  const handleSectorSelect = (sector: Sector) => {
    setSelectedSector(sector);
    setSelectedSubSectorIds([]);
    if (!sector.isHierarchical) {
      loadSectorRisks(sector.id);
    }
  };

  const toggleSubSector = (id: string) => {
    setSelectedSubSectorIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleRisk = (id: string) => {
    setActiveRisks(prev =>
      prev.map(r => (r.id === id ? { ...r, selected: !r.selected } : r))
    );
  };

  const updateScore = (id: string, field: 'olasilik' | 'siddet' | 'frekans', val: number) => {
    setActiveRisks(prev =>
      prev.map(r => (r.id === id ? { ...r, [field]: val } : r))
    );
  };

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGroup = (cat: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const removeRisk = (id: string) => {
    setActiveRisks(prev => prev.filter(r => r.id !== id));
  };

  const addCustomRisk = () => {
    setCustomRiskErrors({});
    const errors: Record<string, string> = {};

    if (!customRisk.kategori || customRisk.kategori.trim() === '') {
      errors.kategori = 'Kategori zorunludur';
    }
    if (!customRisk.tehlike || customRisk.tehlike.trim() === '') {
      errors.tehlike = 'Tehlike / Risk açıklaması zorunludur';
    }
    if (!customRisk.olasiSonuclar || customRisk.olasiSonuclar.trim() === '') {
      errors.olasiSonuclar = 'Olası sonuçlar alanı zorunludur';
    }

    if (Object.keys(errors).length > 0) {
      setCustomRiskErrors(errors);
      return;
    }

    const newRisk: Risk = {
      ...customRisk,
      id: `CUSTOM_${Date.now()}`,
      selected: true,
      isCustom: true
    };

    setActiveRisks(prev => [...prev, newRisk]);
    setCustomRisk({ ...BLANK_CUSTOM });
    setShowCustomForm(false);
    setCustomRiskErrors({});
  };

  const handleGeneratePdf = async () => {
    if (!selectedWp) { setError('Lütfen bir işyeri seçin.'); return; }
    const toExport = activeRisks.filter(r => r.selected !== false);
    if (toExport.length === 0) { setError('En az 1 risk kalemi seçili olmalıdır.'); return; }

    setGenerating(true);
    setError('');
    try {
      const safeName = (selectedWp.name || selectedWp.title || 'Isyeri').replace(/[^a-zA-Z0-9_\-]/g, '_');
      const filename = `risk_analizi_${safeName}_${new Date().getFullYear()}.pdf`;

      await riskAnalysisApi.downloadPdf({
        workplaceId: selectedWp.id,
        matrixType,
        sektorId: selectedSector?.id,
        risks: toExport,
        expert: expertName ? { name: expertName, certificateNo: expertCertNo } : undefined,
        doctor: doctorName ? { name: doctorName, diplomaNo: doctorDipNo } : undefined,
        employer: employerName ? { name: employerName } : undefined,
        destekElemani: destekElemani || undefined,
        calisanTemsilcisi: calisanTemsilcisi || undefined
      }, filename);
    } catch (err: any) {
      console.error('PDF error:', err);
      setError(err.message || 'PDF oluşturulamadı.');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateExcel = async () => {
    if (!selectedWp) { setError('Lütfen bir işyeri seçin.'); return; }
    const toExport = activeRisks.filter(r => r.selected !== false);
    if (toExport.length === 0) { setError('En az 1 risk kalemi seçili olmalıdır.'); return; }

    setGeneratingExcel(true);
    setError('');
    try {
      const safeName = (selectedWp.name || selectedWp.title || 'Isyeri').replace(/[^a-zA-Z0-9_\-]/g, '_');
      const filename = `risk_analizi_${safeName}_${new Date().getFullYear()}.xlsx`;

      await riskAnalysisApi.downloadExcel({
        workplaceId: selectedWp.id,
        matrixType,
        sektorId: selectedSector?.id,
        risks: toExport,
        expert: expertName ? { name: expertName, certificateNo: expertCertNo } : undefined,
        doctor: doctorName ? { name: doctorName, diplomaNo: doctorDipNo } : undefined,
        employer: employerName ? { name: employerName } : undefined,
        destekElemani: destekElemani || undefined,
        calisanTemsilcisi: calisanTemsilcisi || undefined
      }, filename);
    } catch (err: any) {
      console.error('Excel error:', err);
      setError(err.message || 'Excel oluşturulamadı.');
    } finally {
      setGeneratingExcel(false);
    }
  };

  // Step 4 Calculations
  const kategoriler = useMemo(
    () => Array.from(new Set(activeRisks.map(r => r.kategori))).sort(),
    [activeRisks]
  );

  const filteredRisks = useMemo(() => {
    const q = riskSearch.trim().toLowerCase();
    return activeRisks.filter(r => {
      const matchSearch =
        !q ||
        (r.tehlike || '').toLowerCase().includes(q) ||
        (r.kategori || '').toLowerCase().includes(q) ||
        (r.tehlikeKaynagi || '').toLowerCase().includes(q) ||
        (r.olasiSonuclar || '').toLowerCase().includes(q);
      const matchKat = !filterKategori || r.kategori === filterKategori;
      const matchSev = !filterSeviye || scoreLabel(calcScore(r, matrixType), matrixType).label === filterSeviye;
      return matchSearch && matchKat && matchSev;
    });
  }, [activeRisks, riskSearch, filterKategori, filterSeviye, matrixType]);

  const groupedRisks = useMemo(() => {
    const map: Record<string, Risk[]> = {};
    filteredRisks.forEach(r => {
      if (!map[r.kategori]) map[r.kategori] = [];
      map[r.kategori].push(r);
    });
    return map;
  }, [filteredRisks]);

  const selectedCount = activeRisks.filter(r => r.selected !== false).length;

  const scoreSummary = useMemo(() => {
    const counts: Record<string, number> = {};
    SCORE_LEVELS.forEach(l => { counts[l] = 0; });
    activeRisks
      .filter(r => r.selected !== false)
      .forEach(r => {
        const lbl = scoreLabel(calcScore(r, matrixType), matrixType).label;
        counts[lbl] = (counts[lbl] || 0) + 1;
      });
    return counts;
  }, [activeRisks, matrixType]);

  const selectAllByLevel = (level: string) => {
    setActiveRisks(prev =>
      prev.map(r => {
        const lbl = scoreLabel(calcScore(r, matrixType), matrixType).label;
        return lbl === level ? { ...r, selected: true } : r;
      })
    );
  };

  const wpName = selectedWp?.name || selectedWp?.title || '';
  const wpRisk = (selectedWp?.dangerClass || selectedWp?.riskLevel || 'dangerous') as string;

  return (
    <div className="ra-page">
      {/* Top Header */}
      <div className="ra-header">
        <div className="ra-header-left">
          <span className="ra-header-icon">⚠️</span>
          <div>
            <h1 className="ra-title">Risk Analizi ve Değerlendirmesi</h1>
            <p className="ra-subtitle">6331 Sayılı İSG Kanunu Madde 10 Kapsamında Sektörel Standart Risk Değerlendirmesi</p>
          </div>
        </div>
        <button
          className="ra-btn-secondary"
          onClick={() => navigate('/documents')}
        >
          ← Belge Yönetimine Dön
        </button>
      </div>

      {/* 5-Step Stepper Bar */}
      <div className="ra-stepper">
        {['İşyeri Seçimi', 'Analiz Metodu', 'Sektör Seçimi', 'Risk Tablosu', 'PDF & Rapor'].map((s, i) => (
          <div
            key={i}
            onClick={() => {
              const target = i + 1;
              if (target === 1) setStep(1);
              if (target === 2 && selectedWp) setStep(2);
              if (target === 3 && selectedWp) setStep(3);
              if (target === 4 && selectedWp && selectedSector) setStep(4);
              if (target === 5 && selectedWp && selectedSector && selectedCount > 0) setStep(5);
            }}
            className={`ra-step ${step === i + 1 ? 'active' : ''} ${step > i + 1 ? 'done' : ''}`}
            style={{ cursor: 'pointer' }}
          >
            <div className="ra-step-num">{step > i + 1 ? '✓' : i + 1}</div>
            <div className="ra-step-label">{s}</div>
          </div>
        ))}
      </div>

      {error && (
        <div className="ra-error">
          {error}
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}

      <div className="ra-content">
        {/* STEP 1: İşyeri Seçimi */}
        {step === 1 && (
          <div className="ra-card">
            <h2 className="ra-card-title">1. İşyeri Seçin</h2>
            <p className="text-xs text-slate-500 mb-2">
              Risk analizi yapılacak işyerini arayın ve seçin:
            </p>

            {wpLoading ? (
              <div className="ra-spinner" />
            ) : (
              <div className="ra-wp-search-wrap" ref={searchWrapRef}>
                <div className="ra-wp-search-box">
                  <span className="ra-wp-search-icon">🔍</span>
                  <input
                    className="ra-wp-search-input"
                    type="text"
                    placeholder="İşyeri adı veya SGK Sicil No ile arayın..."
                    value={searchQuery}
                    onChange={e => {
                      setSearchQuery(e.target.value);
                      setDropdownOpen(true);
                    }}
                    onFocus={e => {
                      e.target.select();
                      setDropdownOpen(true);
                    }}
                    autoComplete="off"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="ra-wp-search-clear"
                      title="Temizle"
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedWp(null);
                        setDropdownOpen(true);
                      }}
                    >
                      ✕
                    </button>
                  )}
                  <button
                    type="button"
                    className="ra-wp-search-toggle"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 8px', color: '#64748b', fontSize: '13px' }}
                    title="İşyeri Listesini Aç / Kapat"
                    onClick={() => setDropdownOpen(prev => !prev)}
                  >
                    ▾
                  </button>
                </div>

                {dropdownOpen && (
                  <div className="ra-wp-dropdown">
                    {filteredWorkplaces.length === 0 ? (
                      <div className="ra-wp-dropdown-empty">Eşleşen işyeri bulunamadı.</div>
                    ) : (
                      filteredWorkplaces.map(wp => (
                        <div
                          key={wp.id}
                          className={`ra-wp-dropdown-item ${selectedWp?.id === wp.id ? 'active' : ''}`}
                          onClick={() => handleSelectWorkplace(wp)}
                        >
                          <div className="ra-wp-dropdown-main">
                            <span className="ra-wp-dropdown-name">
                              <WorkplaceNameDisplay name={wp.name || wp.title} />
                            </span>
                            <span className={`ra-risk-badge ra-risk-${wp.dangerClass || wp.riskLevel}`}>
                              {RISK_LEVEL_LABEL[wp.dangerClass || wp.riskLevel || 'dangerous']}
                            </span>
                          </div>
                          <div className="ra-wp-dropdown-sub">
                            <span>SGK: <strong>{wp.sskRegistrationNo || wp.sgkNo || '—'}</strong></span>
                            {(wp.city || wp.district) && <span>{[wp.city, wp.district].filter(Boolean).join(' / ')}</span>}
                            {wp.employeeCount && <span>👥 {wp.employeeCount} Çalışan</span>}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {selectedWp && (
              <div className="ra-wp-preview">
                <div className="ra-wp-preview-row">
                  <span>İşyeri:</span>
                  <strong><WorkplaceNameDisplay name={wpName} /></strong>
                </div>
                <div className="ra-wp-preview-row">
                  <span>SGK Sicil No:</span>
                  <strong>{selectedWp.sskRegistrationNo || selectedWp.sgkNo || '—'}</strong>
                </div>
                <div className="ra-wp-preview-row">
                  <span>Tehlike Sınıfı:</span>
                  <strong>{RISK_LEVEL_LABEL[wpRisk] || wpRisk}</strong>
                </div>
                <div className="ra-wp-preview-row">
                  <span>Mevzuat Geçerlilik:</span>
                  <strong>{VALIDITY_YEARS[wpRisk] || 4} yıl ({validityDate(wpRisk)} tarihine kadar geçerli)</strong>
                </div>
              </div>
            )}

            <div className="ra-nav">
              <button
                className="ra-btn-primary"
                disabled={!selectedWp}
                onClick={() => setStep(2)}
              >
                Devam Et →
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Analiz Metodu */}
        {step === 2 && (
          <div className="ra-card">
            <h2 className="ra-card-title">2. Risk Değerlendirme Metodu Seçin</h2>
            <p className="text-xs text-slate-500 mb-4">
              İşyeriniz için kullanmak istediğiniz risk analiz metodolojisini belirleyin:
            </p>

            <div className="ra-matrix-grid">
              {MATRIX_OPTIONS.map(m => (
                <div
                  key={m.id}
                  className={`ra-matrix-card ${matrixType === m.id ? 'selected' : ''}`}
                  onClick={() => setMatrixType(m.id)}
                >
                  <div className="ra-matrix-name">{m.label}</div>
                  <div className="ra-matrix-desc">{m.desc}</div>
                  {matrixType === m.id && <div className="ra-check">✓</div>}
                </div>
              ))}
            </div>

            <div className="ra-nav">
              <button className="ra-btn-secondary" onClick={() => setStep(1)}>
                ← Geri
              </button>
              <button className="ra-btn-primary" onClick={() => setStep(3)}>
                Devam Et →
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Sektör Seçimi */}
        {step === 3 && (
          <div className="ra-sector-step">
            <div className="ra-sector-step-header" style={{ border: '2px solid #94a3b8' }}>
              <div style={{ flex: 1 }}>
                <h2 className="ra-card-title" style={{ margin: 0, border: 'none', padding: 0 }}>
                  3. Sektör / Faaliyet Alanı Seçin
                </h2>
                <p className="text-xs text-slate-500 mt-1 mb-0">
                  İşyerinizin ana faaliyet sektörünü seçerek mevzuata uygun standart risk havuzunu yükleyin:
                </p>
              </div>

              <div className="ra-wp-search-box" style={{ flex: 1, maxWidth: 360 }}>
                <span className="ra-wp-search-icon">🔍</span>
                <input
                  className="ra-wp-search-input"
                  type="text"
                  placeholder="Sektör adıyla filtreleyin..."
                  value={sectorSearch}
                  onChange={e => setSectorSearch(e.target.value)}
                  autoComplete="off"
                />
                {sectorSearch && (
                  <button className="ra-wp-search-clear" onClick={() => setSectorSearch('')}>
                    ✕
                  </button>
                )}
              </div>
            </div>

            {selectedSector && (
              <div className="ra-sector-selected-bar">
                ✅ Seçili Sektör: <strong>{selectedSector.icon} {selectedSector.name}</strong>
                &nbsp;—&nbsp;{sectors.find(s => s.id === selectedSector.id)?.riskCount || 0} risk kalemi
              </div>
            )}

            {sectorsLoading ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div className="ra-spinner" style={{ margin: '0 auto 16px' }} />
                <p className="text-sm font-semibold text-slate-600">Sektörler ve standart risk kütüphanesi yükleniyor...</p>
              </div>
            ) : sectors.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', border: '2px dashed #94a3b8', borderRadius: '8px', background: '#f8fafc' }}>
                <p className="text-sm font-bold text-slate-700 mb-3">Sektör listesi henüz yüklenemedi.</p>
                <button type="button" className="ra-btn-primary" onClick={loadSectors}>
                  🔄 Sektörleri Tekrar Yükle
                </button>
              </div>
            ) : (
              <div className="ra-sector-grid">
                {sectors
                  .filter(s =>
                    normalizeSearch(s.name).includes(normalizeSearch(sectorSearch))
                  )
                  .map(s => (
                    <div
                      key={s.id}
                      className={`ra-sector-card ${selectedSector?.id === s.id ? 'selected' : ''}`}
                      onClick={() => handleSectorSelect(s)}
                      style={{ border: '2px solid #94a3b8' }}
                    >
                      <span className="ra-sector-icon">{s.icon}</span>
                      <div className="ra-sector-name">{s.name}</div>
                      <div className="ra-sector-count">{s.riskCount} standart risk</div>
                      {selectedSector?.id === s.id && <div className="ra-check">✓</div>}
                    </div>
                  ))}
              </div>
            )}

            {selectedSector?.isHierarchical && selectedSector.subSectors && (
              <div className="ra-subsector-section">
                <h3 className="ra-subsector-title">
                  {selectedSector.name} için Alt Başlıklar
                  <button
                    className="ra-btn-link"
                    onClick={() => {
                      if (selectedSubSectorIds.length === selectedSector.subSectors?.length) {
                        setSelectedSubSectorIds([]);
                      } else {
                        setSelectedSubSectorIds(selectedSector.subSectors?.map(ss => ss.id) || []);
                      }
                    }}
                  >
                    {selectedSubSectorIds.length === selectedSector.subSectors?.length
                      ? 'Tüm Seçimi Kaldır'
                      : 'Tümünü Seç'}
                  </button>
                </h3>
                <div className="ra-subsector-grid">
                  {selectedSector.subSectors.map(ss => (
                    <label
                      key={ss.id}
                      className={`ra-subsector-label ${selectedSubSectorIds.includes(ss.id) ? 'active' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSubSectorIds.includes(ss.id)}
                        onChange={() => toggleSubSector(ss.id)}
                      />
                      <span className="ra-subsector-name">{ss.name}</span>
                      <span className="ra-subsector-count">({ss.riskCount} risk)</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {sectorLoading && <div className="ra-spinner" />}

            <div className="ra-nav ra-sector-nav" style={{ border: '2px solid #94a3b8' }}>
              <button className="ra-btn-secondary" onClick={() => setStep(2)}>
                ← Metot Seçimi (Geri)
              </button>
              <button
                className="ra-btn-primary"
                disabled={
                  !selectedSector ||
                  (selectedSector.isHierarchical && selectedSubSectorIds.length === 0) ||
                  sectorLoading
                }
                onClick={async () => {
                  if (selectedSector?.isHierarchical) {
                    await loadSectorRisks(selectedSubSectorIds);
                  }
                  setStep(4);
                }}
              >
                Devam Et (Risk Tablosu) →
              </button>
            </div>
          </div>
        )}

        {/* STEP 4: Risk Tablosu */}
        {step === 4 && (
          <div className="ra-card ra-card-wide">
            <div className="ra-risk-header">
              <div>
                <h2 className="ra-card-title" style={{ margin: 0, padding: 0, border: 'none' }}>
                  4. Risk Değerlendirme Tablosu
                </h2>
                <p className="text-xs text-slate-500 mt-1 mb-0">
                  {selectedSector?.name} sektörüne ait standart riskler listelendi. Olasılık ve şiddet puanlarını inceleyebilir veya yeni risk ekleyebilirsiniz:
                </p>
              </div>
              <div className="ra-risk-stats">
                <span className="ra-badge">{selectedCount} seçili / {activeRisks.length} toplam</span>
                <span className="ra-badge ra-badge-matrix">
                  {MATRIX_OPTIONS.find(m => m.id === matrixType)?.label}
                </span>
              </div>
            </div>

            {/* Score Bar */}
            <div className="ra-score-bar">
              {SCORE_LEVELS.map((lbl, idx) => {
                const cnt = activeRisks.filter(
                  r => r.selected !== false && scoreLabel(calcScore(r, matrixType), matrixType).label === lbl
                ).length;
                return (
                  <div
                    key={lbl}
                    className={`ra-score-bar-item ${SCORE_CLS[idx]}`}
                    title={`${lbl}: ${cnt} risk — filtrelemek / seçmek için tıklayın`}
                    onClick={() => selectAllByLevel(lbl)}
                  >
                    <span className="ra-score-bar-count">{cnt}</span>
                    <span className="ra-score-bar-label">{lbl}</span>
                  </div>
                );
              })}
            </div>

            {/* Filters */}
            <div className="ra-filter-bar">
              <div className="ra-filter-search">
                <span>🔍</span>
                <input
                  type="text"
                  placeholder="Tehlike veya kategori ara..."
                  value={riskSearch}
                  onChange={e => setRiskSearch(e.target.value)}
                />
                {riskSearch && <button onClick={() => setRiskSearch('')}>✕</button>}
              </div>

              <select
                className="ra-filter-select"
                value={filterKategori}
                onChange={e => setFilterKategori(e.target.value)}
              >
                <option value="">Tüm Kategoriler ({kategoriler.length})</option>
                {kategoriler.map(k => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </select>

              <select
                className="ra-filter-select"
                value={filterSeviye}
                onChange={e => setFilterSeviye(e.target.value)}
              >
                <option value="">Tüm Risk Seviyeleri</option>
                {SCORE_LEVELS.map(l => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>

              <div className="ra-filter-actions">
                <button
                  className="ra-btn-outline"
                  onClick={() => setActiveRisks(prev => prev.map(r => ({ ...r, selected: true })))}
                >
                  Tümünü Seç
                </button>
                <button
                  className="ra-btn-outline"
                  onClick={() => setActiveRisks(prev => prev.map(r => ({ ...r, selected: false })))}
                >
                  Tümünü Kaldır
                </button>
                <button
                  className="ra-btn-add"
                  onClick={() => setShowCustomForm(!showCustomForm)}
                >
                  + Özel Risk Ekle
                </button>
              </div>
            </div>

            {/* Custom Risk Modal/Form */}
            {showCustomForm && (
              <div className="ra-custom-form">
                <h3>Yeni Özel Risk Ekle</h3>
                <div className="ra-cf-grid">
                  <div className="ra-cf-field">
                    <input
                      placeholder="Kategori (örn: Kimyasal, Yangın, Ergonomi) *"
                      value={customRisk.kategori}
                      onChange={e => setCustomRisk(p => ({ ...p, kategori: e.target.value }))}
                    />
                    {customRiskErrors.kategori && <span className="ra-cf-error">{customRiskErrors.kategori}</span>}
                  </div>
                  <div className="ra-cf-field">
                    <input
                      placeholder="Tehlike Kaynağı"
                      value={customRisk.tehlikeKaynagi}
                      onChange={e => setCustomRisk(p => ({ ...p, tehlikeKaynagi: e.target.value }))}
                    />
                  </div>
                  <div className="ra-cf-field">
                    <input
                      placeholder="Tehlike / Risk Açıklaması *"
                      value={customRisk.tehlike}
                      onChange={e => setCustomRisk(p => ({ ...p, tehlike: e.target.value }))}
                    />
                    {customRiskErrors.tehlike && <span className="ra-cf-error">{customRiskErrors.tehlike}</span>}
                  </div>
                  <div className="ra-cf-field">
                    <input
                      placeholder="Olası Sonuçlar / Zarar *"
                      value={customRisk.olasiSonuclar}
                      onChange={e => setCustomRisk(p => ({ ...p, olasiSonuclar: e.target.value }))}
                    />
                    {customRiskErrors.olasiSonuclar && <span className="ra-cf-error">{customRiskErrors.olasiSonuclar}</span>}
                  </div>
                  <div className="ra-cf-field">
                    <input
                      placeholder="Mevcut Önlemler"
                      value={customRisk.mevcutOnlemler as string}
                      onChange={e => setCustomRisk(p => ({ ...p, mevcutOnlemler: e.target.value }))}
                    />
                  </div>
                  <div className="ra-cf-field">
                    <input
                      placeholder="Önerilen Düzeltici Önlemler"
                      value={customRisk.onerilen as string}
                      onChange={e => setCustomRisk(p => ({ ...p, onerilen: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="ra-cf-scores">
                  <label>
                    Olasılık:
                    <select
                      value={customRisk.olasilik}
                      onChange={e => setCustomRisk(p => ({ ...p, olasilik: +e.target.value }))}
                    >
                      {matrixType === 'fineKinney'
                        ? [0.2, 0.5, 1, 3, 6, 10].map(v => <option key={v} value={v}>{v}</option>)
                        : [1, 2, 3, 4, 5].map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </label>
                  <label>
                    Şiddet:
                    <select
                      value={customRisk.siddet}
                      onChange={e => setCustomRisk(p => ({ ...p, siddet: +e.target.value }))}
                    >
                      {matrixType === 'fineKinney'
                        ? [1, 3, 7, 15, 40, 100].map(v => <option key={v} value={v}>{v}</option>)
                        : [1, 2, 3, 4, 5].map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </label>
                  {matrixType === 'fineKinney' && (
                    <label>
                      Frekans:
                      <select
                        value={customRisk.frekans}
                        onChange={e => setCustomRisk(p => ({ ...p, frekans: +e.target.value }))}
                      >
                        {[0.5, 1, 2, 3, 6, 10].map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </label>
                  )}
                </div>

                <div className="ra-cf-actions">
                  <button className="ra-btn-primary" onClick={addCustomRisk}>
                    + Listeye Ekle
                  </button>
                  <button className="ra-btn-secondary" onClick={() => setShowCustomForm(false)}>
                    İptal
                  </button>
                </div>
              </div>
            )}

            {/* Grouped Risk Table */}
            <div className="ra-grouped-table">
              {Object.keys(groupedRisks).length === 0 ? (
                <div className="ra-empty">Filtreye uyan risk bulunamadı.</div>
              ) : (
                Object.entries(groupedRisks).map(([kategori, risks]) => {
                  const isCollapsed = collapsedGroups.has(kategori);
                  const groupSelected = risks.filter(r => r.selected !== false).length;

                  return (
                    <div key={kategori} className="ra-group" style={{ border: '2px solid #94a3b8' }}>
                      <div className="ra-group-header" onClick={() => toggleGroup(kategori)}>
                        <span className="ra-group-toggle">{isCollapsed ? '▶' : '▼'}</span>
                        <span className="ra-group-name">{kategori}</span>
                        <span className="ra-group-count">{groupSelected}/{risks.length} seçili</span>
                        <div className="ra-group-actions" onClick={e => e.stopPropagation()}>
                          <button
                            className="ra-btn-link-sm"
                            onClick={() =>
                              setActiveRisks(prev =>
                                prev.map(r => (r.kategori === kategori ? { ...r, selected: true } : r))
                              )
                            }
                          >
                            Tümünü Seç
                          </button>
                          <button
                            className="ra-btn-link-sm"
                            onClick={() =>
                              setActiveRisks(prev =>
                                prev.map(r => (r.kategori === kategori ? { ...r, selected: false } : r))
                              )
                            }
                          >
                            Kaldır
                          </button>
                        </div>
                      </div>

                      {!isCollapsed && (
                        <table className="ra-table">
                          <thead>
                            <tr>
                              <th style={{ width: '36px' }}></th>
                              <th style={{ width: '36px' }}>#</th>
                              <th>Tehlike Kaynağı</th>
                              <th>Tehlike / Risk Tanımı</th>
                              <th>Olası Zarar & Sonuç</th>
                              <th style={{ width: '48px' }}>O</th>
                              <th style={{ width: '48px' }}>Ş</th>
                              {matrixType === 'fineKinney' && <th style={{ width: '48px' }}>F</th>}
                              <th style={{ width: '64px' }}>Skor</th>
                              <th style={{ width: '90px' }}>Seviye</th>
                              <th style={{ width: '40px' }}></th>
                              <th style={{ width: '36px' }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {risks.map((r, i) => {
                              const score = calcScore(r, matrixType);
                              const lvl = scoreLabel(score, matrixType);
                              const isExpanded = expandedRows.has(r.id);
                              const toList = (v: string | string[]) =>
                                Array.isArray(v) ? v : v ? [v] : [];

                              return (
                                <React.Fragment key={r.id}>
                                  <tr className={r.selected === false ? 'ra-row-disabled' : ''}>
                                    <td>
                                      <input
                                        type="checkbox"
                                        checked={r.selected !== false}
                                        onChange={() => toggleRisk(r.id)}
                                      />
                                    </td>
                                    <td className="ra-td-num">{i + 1}</td>
                                    <td className="ra-td-text">{r.tehlikeKaynagi}</td>
                                    <td className="ra-td-text ra-td-main">{r.tehlike || r.riskAciklamasi}</td>
                                    <td className="ra-td-text">{r.olasiSonuclar}</td>
                                    <td>
                                      <select
                                        className="ra-score-sel"
                                        value={r.olasilik}
                                        onChange={e => updateScore(r.id, 'olasilik', +e.target.value)}
                                      >
                                        {matrixType === 'fineKinney'
                                          ? [0.2, 0.5, 1, 3, 6, 10].map(v => <option key={v} value={v}>{v}</option>)
                                          : [1, 2, 3, 4, 5].map(v => <option key={v} value={v}>{v}</option>)}
                                      </select>
                                    </td>
                                    <td>
                                      <select
                                        className="ra-score-sel"
                                        value={r.siddet}
                                        onChange={e => updateScore(r.id, 'siddet', +e.target.value)}
                                      >
                                        {matrixType === 'fineKinney'
                                          ? [1, 3, 7, 15, 40, 100].map(v => <option key={v} value={v}>{v}</option>)
                                          : [1, 2, 3, 4, 5].map(v => <option key={v} value={v}>{v}</option>)}
                                      </select>
                                    </td>
                                    {matrixType === 'fineKinney' && (
                                      <td>
                                        <select
                                          className="ra-score-sel"
                                          value={r.frekans}
                                          onChange={e => updateScore(r.id, 'frekans', +e.target.value)}
                                        >
                                          {[0.5, 1, 2, 3, 6, 10].map(v => <option key={v} value={v}>{v}</option>)}
                                        </select>
                                      </td>
                                    )}
                                    <td><span className={`ra-score ${lvl.cls}`}>{score}</span></td>
                                    <td><span className={`ra-level ${lvl.cls}`}>{lvl.label}</span></td>
                                    <td>
                                      <button
                                        className="ra-expand-btn"
                                        title={isExpanded ? 'Gizle' : 'Detay Göster'}
                                        onClick={() => toggleRow(r.id)}
                                      >
                                        {isExpanded ? '▲' : '▼'}
                                      </button>
                                    </td>
                                    <td>
                                      <button
                                        className="ra-rm-btn"
                                        title="Kaldır"
                                        onClick={() => removeRisk(r.id)}
                                      >
                                        ✕
                                      </button>
                                    </td>
                                  </tr>
                                  {isExpanded && (
                                    <tr className="ra-detail-row">
                                      <td colSpan={matrixType === 'fineKinney' ? 12 : 11}>
                                        <div className="ra-detail-panel">
                                          {r.riskAciklamasi && (
                                            <div className="ra-detail-section">
                                              <strong>📝 Risk Açıklaması</strong>
                                              <p>{r.riskAciklamasi}</p>
                                            </div>
                                          )}
                                          <div className="ra-detail-cols">
                                            {toList(r.mevcutOnlemler).length > 0 && (
                                              <div className="ra-detail-section">
                                                <strong>🛡️ Mevcut Önlemler</strong>
                                                <ul>
                                                  {toList(r.mevcutOnlemler).map((o, j) => (
                                                    <li key={j}>{o}</li>
                                                  ))}
                                                </ul>
                                              </div>
                                            )}
                                            {toList(r.onerilen).length > 0 && (
                                              <div className="ra-detail-section">
                                                <strong>✅ Önerilen Önlemler</strong>
                                                <ul>
                                                  {toList(r.onerilen).map((o, j) => (
                                                    <li key={j}>{o}</li>
                                                  ))}
                                                </ul>
                                              </div>
                                            )}
                                          </div>
                                          {r.kanunMaddeleri && r.kanunMaddeleri.length > 0 && (
                                            <div className="ra-detail-section">
                                              <strong>⚖️ Yasal Dayanak & Mevzuat</strong>
                                              <div className="ra-kanun-tags">
                                                {r.kanunMaddeleri.map((k: any, j: number) => (
                                                  <span key={j} className="ra-kanun-tag">
                                                    {k.kanun} — {k.madde}
                                                  </span>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <div className="ra-nav">
              <button className="ra-btn-secondary" onClick={() => setStep(3)}>
                ← Sektör Seçimi (Geri)
              </button>
              <button
                className="ra-btn-primary"
                disabled={selectedCount === 0}
                onClick={() => setStep(5)}
              >
                Devam Et (Rapor & PDF) →
              </button>
            </div>
          </div>
        )}

        {/* STEP 5: Rapor Özeti ve PDF/Excel İndirme */}
        {step === 5 && selectedWp && (
          <div className="ra-card">
            <h2 className="ra-card-title">5. Risk Analizi Rapor Özeti ve Belge Üretimi</h2>

            <div className="ra-preview-grid">
              <div className="ra-preview-item">
                <span>İşyeri</span>
                <strong><WorkplaceNameDisplay name={wpName} /></strong>
              </div>
              <div className="ra-preview-item">
                <span>Tehlike Sınıfı</span>
                <strong>{RISK_LEVEL_LABEL[wpRisk] || wpRisk}</strong>
              </div>
              <div className="ra-preview-item">
                <span>Analiz Metodu</span>
                <strong>{MATRIX_OPTIONS.find(m => m.id === matrixType)?.label}</strong>
              </div>
              <div className="ra-preview-item">
                <span>Sektör</span>
                <strong>{selectedSector?.name || '-'}</strong>
              </div>
              <div className="ra-preview-item">
                <span>Risk Kalemi Sayısı</span>
                <strong>{selectedCount} Seçili</strong>
              </div>
              <div className="ra-preview-item">
                <span>Geçerlilik Süresi</span>
                <strong>{VALIDITY_YEARS[wpRisk] || 4} Yıl — {validityDate(wpRisk)}</strong>
              </div>
            </div>

            <div className="ra-score-summary">
              {SCORE_LEVELS.map((lbl, idx) => (
                <div key={lbl} className={`ra-sum-item ${SCORE_CLS[idx]}`}>
                  <div className="ra-sum-count">{scoreSummary[lbl] || 0}</div>
                  <div className="ra-sum-label">{lbl}</div>
                </div>
              ))}
            </div>

            {/* Risk Değerlendirme Ekibi (Resmi Belge Onay Heyeti) */}
            <div className="ra-team-section" style={{
              marginTop: '16px',
              padding: '16px',
              backgroundColor: '#f8fafc',
              border: '1.5px solid #e2e8f0',
              borderRadius: '10px'
            }}>
              <h3 style={{
                fontSize: '14px',
                fontWeight: 700,
                color: '#1e293b',
                margin: '0 0 10px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>👥</span> Risk Değerlendirme Ekibi (Resmi İmza ve Onay Heyeti)
              </h3>
              <p style={{ fontSize: '11.5px', color: '#64748b', margin: '0 0 14px' }}>
                Resmi PDF raporu ve Excel kapak sayfasına eklenecek onay heyeti bilgilerini kontrol ediniz:
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '12px'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11.5px', fontWeight: 600, color: '#334155' }}>
                    🛡️ İş Güvenliği Uzmanı
                  </label>
                  <input
                    type="text"
                    value={expertName}
                    onChange={e => setExpertName(e.target.value)}
                    placeholder="Uzman Ad Soyad"
                    style={{
                      padding: '7px 10px',
                      fontSize: '12.5px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px'
                    }}
                  />
                  <input
                    type="text"
                    value={expertCertNo}
                    onChange={e => setExpertCertNo(e.target.value)}
                    placeholder="Belge / Sicil No"
                    style={{
                      padding: '5px 10px',
                      fontSize: '11.5px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      color: '#475569'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11.5px', fontWeight: 600, color: '#334155' }}>
                    🩺 İşyeri Hekimi
                  </label>
                  <input
                    type="text"
                    value={doctorName}
                    onChange={e => setDoctorName(e.target.value)}
                    placeholder="Hekim Ad Soyad"
                    style={{
                      padding: '7px 10px',
                      fontSize: '12.5px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px'
                    }}
                  />
                  <input
                    type="text"
                    value={doctorDipNo}
                    onChange={e => setDoctorDipNo(e.target.value)}
                    placeholder="Diploma No"
                    style={{
                      padding: '5px 10px',
                      fontSize: '11.5px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      color: '#475569'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11.5px', fontWeight: 600, color: '#334155' }}>
                    🏢 İşveren / İşveren Vekili
                  </label>
                  <input
                    type="text"
                    value={employerName}
                    onChange={e => setEmployerName(e.target.value)}
                    placeholder="İşveren Yetkili Ad Soyad"
                    style={{
                      padding: '7px 10px',
                      fontSize: '12.5px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11.5px', fontWeight: 600, color: '#334155' }}>
                    ⛑️ Destek Elemanı
                  </label>
                  <input
                    type="text"
                    value={destekElemani}
                    onChange={e => setDestekElemani(e.target.value)}
                    placeholder="Destek Elemanı Ad Soyad"
                    style={{
                      padding: '7px 10px',
                      fontSize: '12.5px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11.5px', fontWeight: 600, color: '#334155' }}>
                    👥 Çalışan Temsilcisi
                  </label>
                  <input
                    type="text"
                    value={calisanTemsilcisi}
                    onChange={e => setCalisanTemsilcisi(e.target.value)}
                    placeholder="Çalışan Temsilcisi Ad Soyad"
                    style={{
                      padding: '7px 10px',
                      fontSize: '12.5px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px'
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="ra-legal-notice">
              <strong>⚖️ Yasal Dayanak:</strong> 6331 Sayılı İSG Kanunu Madde 10 – Risk Değerlendirmesi Yönetmeliği (R.G. 29.12.2012/28512) uyarınca düzenlenmiştir.
            </div>

            <div
              className="ra-legal-notice"
              style={{
                marginTop: '12px',
                backgroundColor: '#fffbeb',
                borderLeft: '4px solid #f59e0b',
                color: '#92400e',
                border: '2px solid #fbbf24'
              }}
            >
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '10px' }}>
                <input
                  type="checkbox"
                  checked={legalChecked}
                  onChange={e => setLegalChecked(e.target.checked)}
                  style={{ width: '18px', height: '18px', flexShrink: 0, cursor: 'pointer' }}
                />
                <span style={{ fontSize: '12.5px', lineHeight: 1.4 }}>
                  <strong>Burada önerilen riskler ve tedbirler sektörel mevzuat standartları olup, mevcut işyerinin sahadaki fiili koşulları dikkate alınarak uzman tarafından kontrol edilmiştir.</strong>
                  <br />
                  <span style={{ color: '#b45309', fontWeight: 700 }}>OKUDUM, ONAYLIYORUM.</span>
                </span>
              </label>
            </div>

            <div className="ra-nav" style={{ marginTop: '20px' }}>
              <button className="ra-btn-secondary" onClick={() => setStep(4)}>
                ← Risk Tablosuna Dön
              </button>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  className="ra-btn-generate"
                  style={{ background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' }}
                  disabled={generatingExcel || generating || !legalChecked}
                  onClick={handleGenerateExcel}
                >
                  {generatingExcel ? '📊 Excel Oluşturuluyor...' : '📊 Excel Şablonu İndir'}
                </button>
                <button
                  className="ra-btn-generate"
                  disabled={generating || generatingExcel || !legalChecked}
                  onClick={handleGeneratePdf}
                >
                  {generating ? '📥 PDF Oluşturuluyor...' : '📥 Resmi PDF Raporu İndir'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiskAnalysisPage;
